import type { Frame, Step } from "./run/working.ts";

export type Logged = {
  run: string;
  turn: number;
  kind: string;
  at: string;
  payload: Record<string, unknown>;
};

export type TurnRequest = { id: string; by: string; text: string; at: string };
export type TurnDelegation = {
  run: string;
  state: string;
  startedAt: string | null;
  endedAt: string | null;
  toolCount: number;
  lastActivityAt: string | null;
};

export type Turn = {
  run: string;
  turn: number;
  state: string;
  request?: TurnRequest | null;
  startedAt: string | null;
  endedAt: string | null;
  spend: number;
  durationMs?: number | null;
  tokens?: number;
  telemetrySpend?: number;
  costKnown?: boolean;
  toolCount?: number;
  currentTool?: { tool: string; object?: string; startedAt: string } | null;
  retries?: number;
  compactions?: number;
  dropped?: number;
  heartbeatAt?: string | null;
  delegations?: TurnDelegation[];

  steps?: Logged[];
};

export type Said = { id: string; at: string; text: string };

export type Row =
  | { key: string; at: string; kind: "started"; turn: number }
  | { key: string; at: string; kind: "waiting"; turn: number }
  | { key: string; at: string; kind: "did"; turn: number; frame: Frame }
  | {
      key: string;
      at: string;
      kind: "ended";
      turn: number;

      finished: boolean;

      how: string | null;
      tools: number;
      spend: number;
    }
  | { key: string; at: string; kind: "said"; text: string };

const RANK: Record<Row["kind"], number> = { ended: 4, did: 3, waiting: 2, started: 1, said: 0 };

function frameIn(payload: Record<string, unknown>): Frame {
  const listed = Array.isArray(payload.did) ? (payload.did as Array<Record<string, unknown>>) : [];
  const did: Step[] = listed.map((one) => ({
    tool: typeof one?.tool === "string" ? one.tool : "a tool",
    ok: one?.ok === true,
  }));
  const more = typeof payload.more === "number" ? payload.more : 0;
  return { did, more };
}

function toolsOf(steps: readonly Logged[], run: string): number {
  let counted = 0;
  for (const step of steps) {
    if (step.run !== run || step.kind !== "run.did") continue;
    const frame = frameIn(step.payload);
    counted += frame.did.length + Math.max(0, frame.more);
  }
  return counted;
}

export function activityRows(
  activity: { turns: readonly Turn[]; steps: readonly Logged[] } | null,
  said: readonly Said[],
  most: number,
): Row[] {
  const rows: Row[] = [];
  const steps = activity?.steps ?? [];
  for (const step of steps) {
    const at = step.at;
    const turn = step.turn;
    if (step.kind === "run.started") rows.push({ key: `${step.run}:started`, at, kind: "started", turn });
    else if (step.kind === "run.waiting")
      rows.push({ key: `${step.run}:waiting:${at}`, at, kind: "waiting", turn });
    else if (step.kind === "run.did") {
      const frame = frameIn(step.payload);

      if (frame.did.length > 0 || frame.more > 0) {
        rows.push({ key: `${step.run}:did:${at}`, at, kind: "did", turn, frame });
      }
    } else if (step.kind === "run.finished" || step.kind === "run.stopped") {
      const one = activity?.turns.find((each) => each.run === step.run);
      rows.push({
        key: `${step.run}:ended`,
        at,
        kind: "ended",
        turn,
        finished: step.kind === "run.finished",
        how: typeof step.payload.how === "string" ? step.payload.how : null,
        tools: toolsOf(steps, step.run),
        spend: one?.spend ?? 0,
      });
    }
  }
  for (const one of said) {
    const text = one.text.trim();
    if (text) rows.push({ key: `said:${one.id}`, at: one.at, kind: "said", text });
  }

  rows.sort((one, two) => {
    const when = Date.parse(two.at) - Date.parse(one.at);
    return when !== 0 ? when : RANK[two.kind] - RANK[one.kind];
  });
  return rows.slice(0, most);
}

export function turnInFlight(activity: { turns: readonly Turn[] } | null): Turn | null {
  return (activity?.turns ?? []).find((one) => one.state === "started" || one.state === "waiting") ?? null;
}
